import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Camera, Download, FileUp } from 'lucide-react';
import { Abas, PainelAba } from '../../../web/src/componentes/abas';
import { Botao, Cartao, Selo } from '../../../web/src/componentes/base';
import { Estado, Vazio } from '../../../web/src/componentes/estados';
import { CampoData, Entrada, Rotulo, Selecao } from '../../../web/src/componentes/formulario';
import { useToast } from '../../../web/src/componentes/toast';
import { ErroRequisicao, mensagemDeErro } from '../../../web/src/nucleo/api';
import { nomeDeArquivo, textoLivre } from '../../../web/src/nucleo/formato';
import { baixar, chamar, pget } from '../api';
import { useSessao } from '../sessao';
import { ParaAssinar } from './Assinar';
import { competenciaCurta, dataCurta, tamanhoArquivo } from './comum';

interface Entrega {
  id: string;
  area: string;
  competencia: string | null;
  titulo: string;
  descricao: string;
  publicado_em: string;
  visto_em: string | null;
  baixado_em: string | null;
  nome: string;
  mime: string;
  tamanho: number;
}

interface Envio {
  id: string;
  area: string;
  tipo: string;
  competencia: string | null;
  descricao: string;
  recebido_em: string | null;
  created_at: string;
  nome: string;
  tamanho: number;
  meu: boolean;
}

const TIPOS = ['extrato', 'nota_fiscal', 'folha_ponto', 'admissao', 'comprovante', 'contrato', 'outro'] as const;

export function ListaEntregas({ aoEnviar }: { aoEnviar?: () => void }) {
  const { t, i18n } = useTranslation('portal');
  const idioma = i18n.language.slice(0, 2);
  const qc = useQueryClient();
  const toast = useToast();
  const s = useSessao();
  const q = useQuery({ queryKey: ['portal', 'entregas'], queryFn: () => pget<{ itens: Entrega[] }>('/documentos/entregas') });
  return (
    <Estado consulta={q}>
      {(d) =>
        d.itens.length ? (
          <ul className="divide-y divide-borda rounded-xl border border-borda bg-sup-1" data-entregas>
            {d.itens.map((e) => (
              <li key={e.id} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    <span className="truncate">{textoLivre(e.titulo, s.empresa.nome)}</span>
                    {!e.visto_em && <Selo cor="acento">{t('documentos.novo')}</Selo>}
                  </p>
                  <p className="truncate text-xs text-tinta-3">
                    {t(`documentos.area.${e.area}`)}
                    {e.competencia ? ` · ${competenciaCurta(e.competencia, idioma)}` : ''} · {t('documentos.publicado', { data: dataCurta(e.publicado_em, idioma) })} · {tamanhoArquivo(e.tamanho)}
                  </p>
                </div>
                <Botao
                  tamanho="sm"
                  icone={<Download className="size-4" aria-hidden />}
                  aria-label={`${t('documentos.baixar')}: ${textoLivre(e.titulo, s.empresa.nome)}`}
                  onClick={async () => {
                    try {
                      await baixar(`/documentos/entregas/${e.id}/arquivo`, e.nome);
                      await qc.invalidateQueries({ queryKey: ['portal', 'entregas'] });
                      await qc.invalidateQueries({ queryKey: ['portal', 'hoje'] });
                    } catch (err) {
                      toast.erro(mensagemDeErro(err));
                    }
                  }}
                >
                  {t('documentos.baixar')}
                </Botao>
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded-xl border border-borda bg-sup-1">
            <Vazio texto={t('documentos.semEntregas')} acao={aoEnviar ? <Botao icone={<FileUp className="size-4" aria-hidden />} onClick={aoEnviar}>{t('documentos.enviarAgora')}</Botao> : undefined} />
          </div>
        )
      }
    </Estado>
  );
}

export function EnviarDocumento() {
  const { t, i18n } = useTranslation('portal');
  const idioma = i18n.language.slice(0, 2);
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [tipo, setTipo] = useState<string>('extrato');
  const [area, setArea] = useState<string>(s.areas.includes('financeiro') ? 'financeiro' : 'geral');
  const [competencia, setCompetencia] = useState('');
  const [descricao, setDescricao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const camera = useRef<HTMLInputElement>(null);
  const seletor = useRef<HTMLInputElement>(null);
  const enviados = useQuery({ queryKey: ['portal', 'envios'], queryFn: () => pget<{ itens: Envio[] }>('/documentos/envios') });
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Trajetórias (item 5): no modo saída não há envio novo (a lista do que já foi enviado fica). */}
      {!s.saida && (
        <Cartao titulo={t('documentos.enviarTitulo')}>
          <form
            className="flex flex-col gap-4"
            noValidate
            onSubmit={async (e) => {
              e.preventDefault();
              if (!arquivo) return;
              setErro(null);
              setEnviando(true);
              try {
                const f = new FormData();
                f.set('tipo', tipo);
                f.set('area', area);
                if (/^\d{4}-\d{2}$/.test(competencia)) f.set('competencia', competencia);
                if (descricao.trim()) f.set('descricao', descricao.trim());
                f.set('arquivo', arquivo, arquivo.name);
                await chamar('POST', '/documentos/envios', f);
                setArquivo(null);
                setDescricao('');
                toast.sucesso(t('documentos.enviado'));
                await qc.invalidateQueries({ queryKey: ['portal', 'envios'] });
              } catch (err) {
                setErro(err instanceof ErroRequisicao ? mensagemDeErro(err) : mensagemDeErro(err));
              } finally {
                setEnviando(false);
              }
            }}
          >
            <p className="text-sm text-tinta-2">{t('documentos.enviarTexto')}</p>
            {erro && (
              <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
                {erro}
              </p>
            )}
            <input ref={camera} type="file" accept="image/*" capture="environment" className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} />
            <input
              ref={seletor}
              type="file"
              accept="application/pdf,image/*,.xml,.csv,.txt,.doc,.docx,.xls,.xlsx,.odt,.ods"
              className="sr-only"
              tabIndex={-1}
              aria-hidden
              onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            />
            <div className="flex flex-wrap gap-2">
              <Botao icone={<Camera className="size-4" aria-hidden />} onClick={() => camera.current?.click()}>
                {t('documentos.camera')}
              </Botao>
              <Botao icone={<FileUp className="size-4" aria-hidden />} onClick={() => seletor.current?.click()}>
                {t('documentos.escolher')}
              </Botao>
            </div>
            {arquivo && (
              <p className="truncate text-sm text-tinta-1" data-arquivo-escolhido>
                {arquivo.name} · {tamanhoArquivo(arquivo.size)}
              </p>
            )}
            <Rotulo id="envio-tipo" rotulo={t('documentos.tipoCampo')}>
              <Selecao id="envio-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)} opcoes={TIPOS.map((x) => ({ valor: x, rotulo: t(`documentos.tipo.${x}`) }))} />
            </Rotulo>
            <Rotulo id="envio-area" rotulo={t('documentos.areaCampo')}>
              <Selecao id="envio-area" value={area} onChange={(e) => setArea(e.target.value)} opcoes={s.areas.map((x) => ({ valor: x, rotulo: t(`documentos.area.${x}`) }))} />
            </Rotulo>
            <Rotulo id="envio-competencia" rotulo={t('documentos.competenciaCampo')}>
              {/* Review r23 visual (M2): mês no idioma do app (o `CampoData` do núcleo), nunca o seletor nativo no idioma do navegador. */}
              <CampoData id="envio-competencia" type="month" value={competencia} onChange={(e) => setCompetencia(e.target.value)} />
            </Rotulo>
            <Rotulo id="envio-descricao" rotulo={t('documentos.descricaoCampo')}>
              <Entrada id="envio-descricao" value={descricao} maxLength={2000} onChange={(e) => setDescricao(e.target.value)} />
            </Rotulo>
            <Botao type="submit" variante="primario" carregando={enviando} disabled={!arquivo || s.somente_leitura}>
              {t('documentos.mandar')}
            </Botao>
          </form>
        </Cartao>
      )}
      <Cartao titulo={t('documentos.enviados')} semPadding>
        <Estado consulta={enviados}>
          {(d) =>
            d.itens.length ? (
              <ul className="divide-y divide-borda" data-envios>
                {d.itens.map((e) => (
                  <li key={e.id} className="flex items-center gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{nomeDeArquivo(e.nome, s.empresa.nome)}</p>
                      <p className="truncate text-xs text-tinta-3">
                        {t(`documentos.tipo.${e.tipo}`)} · {t(`documentos.area.${e.area}`)}
                        {e.competencia ? ` · ${competenciaCurta(e.competencia, idioma)}` : ''} · {dataCurta(e.created_at, idioma)}
                        {e.meu ? ` · ${t('documentos.meu')}` : ''}
                      </p>
                    </div>
                    <Selo cor={e.recebido_em ? 'verde' : 'neutro'}>{e.recebido_em ? t('documentos.recebido') : t('documentos.aguardando')}</Selo>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="p-4 text-sm text-tinta-3">{t('documentos.semEnviados')}</p>
            )
          }
        </Estado>
      </Cartao>
    </div>
  );
}

/**
 * Documentos: o que a Malgo publicou (com a trilha de visto e baixado), os documentos para assinar (Pacote 2, com o módulo
 * Documentos e assinatura ligado no escritório) e o envio pela câmera ou por arquivo. A aba vem da URL (`?aba=assinar`).
 */
export function Documentos() {
  const { t } = useTranslation('portal');
  const s = useSessao();
  const [busca] = useSearchParams();
  const comAssinar = !!s.secoes.assinaturas;
  const pedida = busca.get('aba');
  const [aba, setAba] = useState<'malgo' | 'assinar' | 'enviar'>(pedida === 'assinar' && comAssinar ? 'assinar' : pedida === 'enviar' ? 'enviar' : 'malgo');
  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold tracking-tight">{t('documentos.titulo')}</h1>
      <Abas
        idBase="documentos"
        rotulo={t('documentos.titulo')}
        ativa={aba}
        aoMudar={setAba}
        itens={[
          { chave: 'malgo', rotulo: t('documentos.daMalgo') },
          ...(comAssinar ? [{ chave: 'assinar' as const, rotulo: t('assinar.aba') }] : []),
          { chave: 'enviar', rotulo: t('documentos.enviar') },
        ]}
      />
      <PainelAba idBase="documentos" chave={aba}>
        {aba === 'malgo' ? <ListaEntregas aoEnviar={() => setAba('enviar')} /> : aba === 'assinar' ? <ParaAssinar /> : <EnviarDocumento />}
      </PainelAba>
    </div>
  );
}
